import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  mergeMap,
  map,
  takeUntil,
  catchError,
  tap,
  switchMap,
} from 'rxjs/operators';
import { ConfigService } from './config.service';
import { CookieService } from 'ngx-cookie-service';
import { Role, RoleType } from '../models/role.model';
import { ProfileData } from '../models/profile-data.model';

interface NodeOptions {
  headers: {};
  params: { sessionUserId: string };
}

@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnDestroy {
  bypassAuthUserId: string | null = null; // jsmith
  loggedIn = false;
  isInactive = false;
  sessionData: any;
  maxId: string;
  menuToggles: string[] = [];
  hasNonDesigneeRole = false;
  rolesData: Role[];
  roles: string[] = [];
  profileData: ProfileData;
  nodeSessionData: any;
  protected destroyed$: Subject<boolean> = new Subject();
  public rolesLoaded$: Subject<boolean> = new Subject();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private cookieService: CookieService,
  ) {}

  public static decodeResults(results: any) {
    let out;
    if (results === '' || (results !== null && results.rows == null)) {
      out = [];
    } else if (!!results && results.rows instanceof Array) {
      out = results.rows;
    } else {
      out = [results?.rows];
    }
    // Translate DSS nulls and booleans into native Javascript values...
    for (const i in out) {
      if (out.hasOwnProperty(i)) {
        for (const j in out[i]) {
          if (out[i].hasOwnProperty(j)) {
            if (out[i][j] != null && out[i][j]['@nil'] === 'true') {
              out[i][j] = null;
            } else {
              if (j === 'is_primary' || j === 'is_inherited') {
                out[i][j] = out[i][j] === 'true';
              }
            }
          }
        }
      }
    }
    return out;
  }

  public init() {
    console.log('Initializing SessionService');

    return new Promise<void>(async (resolve, reject) => {
      const user = this.getSession()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(async (data) => {
          this.nodeSessionData = data;

          if (data && data.authenticated) {
            if (this.isAuthBypassed()) {
              const canonical = await this.retrieveCanonicalId().toPromise();
              if (
                canonical !== undefined &&
                canonical.length &&
                canonical[0] !== undefined &&
                canonical[0].id
              ) {
                this.nodeSessionData.user.surrogate_key = canonical[0].id;
              }
            }

            if (this.nodeSessionData.idp === 'max') {
              this.saveProfileData(this.nodeSessionData.attributes);
              if (!this.isInIntegrityAccessGroup()) {
                // Not in MAX Integrity access group
                return resolve();
              }
            }

            this.loggedIn = true;

            forkJoin({
              roles: this.retrieveRoleData(),
              toggles: this.retrieveToggleData(),
            }).subscribe({
              next: (results) => {
                this.parseRoles(results.roles);
                this.parseToggles(results.toggles);

                resolve();
              },
              error: (error) => {
                resolve();
              },
            });
          } else {
            // Not logged in
            resolve();
          }
        });
    });
  }

  /**
   * When a new group is added, refresh the session user's roles to
   * include the new group
   * OGE-8954
   */
  public refreshRoles() {
    this.retrieveRoleData().subscribe({
      next: (roles) => {
        this.parseRoles(roles);
        this.rolesLoaded$.next(true);
      },
    });
  }

  public isLoggedIn() {
    return this.loggedIn;
  }

  public isAuthBypassed(): boolean {
    return !!this.bypassAuthUserId;
  }

  public getRolesData(): Role[] {
    return this.rolesData;
  }

  public getNonFilingRolesData() {
    const nonFilingRoleKeys = [
      'OGE_ADMINISTRATOR',
      'OGE_DIRECTOR',
      'OGE_RECORDS_MANAGER',
      'SYSTEM_ADMINISTRATOR',
      'SYSTEM_SUPERUSER',
    ];
    return this.rolesData.filter((role: any) => {
      return nonFilingRoleKeys.includes(role.role_surrogate_key);
    });
  }

  public getMaxUsername(): string {
    //return this.nodeSessionData?.user.surrogate_key ?? this.profileData.maxId;
    return (
      this.nodeSessionData.canonicalId ??
      this.nodeSessionData?.user?.surrogate_key
    );
  }

  public getMaxFirstname(): string {
    return (
      this.nodeSessionData?.user?.first_name ?? this.profileData?.firstName
    );
  }

  public getMaxLastname(): string {
    return this.nodeSessionData?.user?.last_name ?? this.profileData?.lastName;
  }

  public getMaxEmail(): string {
    return this.nodeSessionData?.user?.email_primary ?? this.profileData?.email;
  }

  public setUploadAuthCookie() {
    /*if (this.casVal) {
      this.cookieService.set(
        'upload-auth-key',
        this.casVal,
        undefined,
        '/',
        undefined,
        true,
        'Strict',
      );
    }*/
  }

  public deleteUploadAuthCookie() {
    //this.cookieService.delete('upload-auth-key', '/');
  }

  private saveProfileData(data: any) {
    this.profileData = ProfileData.fromJson(data);
  }

  private parseToggles(toggleData: any) {
    if (!!toggleData && Array.isArray(toggleData)) {
      for (const toggleD of toggleData) {
        const toggles: string[] =
          !!toggleD && !!toggleD.toggle ? toggleD.toggle.split(',') : [];
        toggles.forEach((toggle: string) => {
          toggle = toggle.toLowerCase();
          if (!this.menuToggles.includes(toggle)) {
            this.menuToggles.push(toggle);
          }
        });
      }
    }

    // Record if the user actually has a role granting menu access
    this.hasNonDesigneeRole = this.menuToggles.length > 0;

    // Users without any roles (such as Designees) get the "filer" tab.
    if (this.menuToggles.length === 0) {
      this.menuToggles.push('filer');
    }
  }

  private parseRoles(rolesData: Role[]) {
    this.rolesData = rolesData;
    for (const role of rolesData) {
      if (role.type === RoleType.Filer && !this.roles.includes('filer')) {
        this.roles.push('filer');
      }
      if (role.type === RoleType.Reviewer && !this.roles.includes('reviewer')) {
        this.roles.push('reviewer');
      }
      if (
        (role.type === RoleType.AgencyAdministrator ||
          role.type === RoleType.PointOfContact ||
          role.type === RoleType.RecordsManager ||
          role.type === RoleType.SystemAdministrator) &&
        !this.roles.includes('admin')
      ) {
        this.roles.push('admin');
      }
    }
  }

  private retrieveRoleData(): Observable<Role[]> {
    return this.http
      .get<any>(
        ConfigService.INTEGRITY_SERVICE_GET_USER_ROLES,
        this.getNodeOptions(),
      )
      .pipe(
        map((result) => {
          return Role.mapDatabaseResult(result);
        }),
      );
  }

  private retrieveToggleData(): Observable<any> {
    return this.http
      .get<any>(
        ConfigService.INTEGRITY_SERVICE_GET_USER_TOGGLES,
        this.getNodeOptions(),
      )
      .pipe(map((result) => SessionService.decodeResults(result.results)));
  }

  private isInIntegrityAccessGroup() {
    return (
      this.profileData.inGroup(ConfigService.INTEGRITY_ACCESS_GROUP_ID) &&
      this.profileData.maxId !== 'INACTIVE'
    );
  }
  public hasFilerMenuToggle() {
    return this.menuToggles.includes('filer');
  }

  public hasAdminMenuToggle() {
    return this.menuToggles.includes('admin');
  }

  public hasReviewerMenuToggle() {
    return this.menuToggles.includes('reviewer');
  }

  public hasRole(target: string, targetAgency: string = ''): boolean {
    let hasRole: boolean = false;
    const rolesData: Array<Role> = this.getRolesData();

    rolesData.forEach((role) => {
      if (!targetAgency && role.surrogateKey == target) {
        hasRole = true;
      }
      if (
        !!targetAgency &&
        role.agencyType == targetAgency &&
        role.surrogateKey == target
      ) {
        hasRole = true;
      }
    });

    return hasRole;
  }

  public hasRoleType(roleType: string) {
    let hasRoleType = false;
    this.roles.forEach((role) => {
      if (roleType === role) {
        hasRoleType = true;
      }
    });
    return hasRoleType;
  }

  public hasRoleKey(roleKey: string): boolean {
    return this.rolesData.reduce((hasRole, role) => {
      return hasRole || role.surrogateKey === roleKey;
    }, false);
  }

  getSessionIdQueryParam(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('sessionId');
  }

  private deleteSession(): Observable<any> {
    //this.cookieService.delete(this.CASKEY);

    // this sessionStorage variable might be used in development
    sessionStorage.removeItem('MAXID');

    if (!this.isAuthBypassed()) {
      this.sessionData = null;
      this.profileData = new ProfileData();
      this.rolesData = [];
    }

    return this.http.delete<any>(ConfigService.INTEGRITY_SERVICE_NODE_SESSION);
  }

  public notifyOtherWindows(message: string) {
    localStorage.setItem('SESSION_STATUS', message);
    // wait a moment for actions to propagate, then clear notification
    setTimeout(() => localStorage.removeItem('SESSION_STATUS'), 3000);
  }

  public logout() {
    this.deleteSession()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {});
    this.notifyOtherWindows('logged out');
  }

  public getNodeOptions(): NodeOptions {
    return {
      headers: this.getNodeHeader(),
      params: {
        sessionUserId:
          this.nodeSessionData?.canonicalId ??
          this.nodeSessionData?.user?.surrogate_key ??
          this.profileData?.maxId,
      },
    };
  }

  public getNodeHeader(): any {
    return {
      'x-session-user-id': this.getMaxUsername(),
    };
  }

  private retrieveCanonicalId(): Observable<any> {
    return this.http
      .get<any>(
        ConfigService.INTEGRITY_SERVICE_GET_CANONICAL_ID,
        this.getNodeOptions(),
      )

      .pipe(
        catchError((error) => {
          this.isInactive = true;
          return error;
        }),
        map((result) => SessionService.decodeResults(result.results)),
      );
  }

  public getSession() {
    if (this.isAuthBypassed()) {
      return of({
        authenticated: true,
        user: {
          surrogate_key: this.bypassAuthUserId,
          last_name: 'Smith',
          first_name: 'John',
        },
        attributes: {
          given_name: 'John',
          family_name: 'Smith',
          sub: 'jsmith',
          acr: [
            'http://idmanagement.gov/ns/assurance/ial/1',
            'http://idmanagement.gov/ns/assurance/aal/2',
          ],
        },
        idp: 'fedhub',
        authLevel: {
          met: true,
        },
      });
    } else {
      return this.http.get<any>(ConfigService.INTEGRITY_SERVICE_NODE_SESSION);
    }
  }

  public updateSession(session: any): Observable<any> {
    return this.http
      .put<any>(ConfigService.INTEGRITY_SERVICE_NODE_SESSION, session)
      .pipe(
        switchMap((initialResponse) => {
          this.nodeSessionData = initialResponse;

          // Short circuit if the required authentication level is not met
          if (!this.nodeSessionData.authLevel.met) {
            return of(initialResponse);
          }

          return forkJoin({
            roles: this.retrieveRoleData(),
            toggles: this.retrieveToggleData(),
          }).pipe(
            // After both dependent requests complete, map the result back to the original response
            map((results) => {
              this.parseRoles(results.roles);
              this.parseToggles(results.toggles);

              return initialResponse;
            }),
          );
        }),
      );
  }

  public getTargetIalLevel(): number {
    return this.nodeSessionData?.authLevel?.minimum;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
